import { Component, Input, OnInit } from '@angular/core';
import { DataFieldTypes } from '@app/enums/data-field-types.enum';
import { AuthService } from '@app/services';
import { BaseForm } from '@forms/base.form';
import { Role } from '@models/account/role.model';
import { DataFieldGroup } from '@models/settings/data-field-group.model';
import { DataFieldOption } from '@models/settings/data-field-option.model';
import { DataField } from '@models/settings/data-field.model';
import { DataFieldOptionStateManager } from '@settings/classes/data-field-option-state-manager.class';

@Component({
    selector: 'app-form-data-field-compact',
    templateUrl: './field-compact.template.html',
    styleUrls: ['./field-compact.style.scss'],
})
export class FieldCompactForm extends BaseForm implements OnInit {
    @Input() field: DataField = new DataField({ isEncrypted: false });
    @Input() group: DataFieldGroup | null = null;
    @Input() isLoading = false;
    allRoles: Role[] = [];
    selectableRoles: Role[] = [];
    viewOnlySelectableRoles: Role[] = [];
    roleIds = [];
    viewOnlyRoleIds = [];
    dataFieldOptionState: DataFieldOptionStateManager;

    stagedGroup: DataFieldGroup = new DataFieldGroup();
    fieldGroups: DataFieldGroup[] = [];
    dataFieldTypes = DataFieldTypes;
    hasMinMax = false;
    componentFilter: string | null = null;
    placeholderInputDisabled = false;

    constructor(private auth: AuthService) {
        super();
    }

    async ngOnInit(): Promise<void> {
        await Promise.all([this.getDataFieldGroups(), this.getRoles()]);
        this.hasMinMax = this.typeHasMinMax(this.field.type);
        this.dataFieldOptionState = new DataFieldOptionStateManager(this.field, this.auth.company.id);
    }

    applyComponentFilter(component: string): FieldCompactForm {
        this.componentFilter = component;
        this.stagedGroup.component = component;
        return this;
    }

    reset(): void {
        this.form?.reset();
        // Need to set isEncrypted value default, which super() reset does not do.
        this.field = new DataField({ isRequired: false, isEncrypted: false });
        this.ngOnInit();
    }

    onRoleChange(roles: Role[]): void {
        this.roleIds = roles.map((role) => role.id);
        this.field.roleIds = this.roleIds;
        this.viewOnlySelectableRoles = this.allRoles.filter((role) => !this.roleIds.includes(role.id));
    }

    onViewOnlyRoleChange(viewOnlyRoles: Role[]): void {
        this.viewOnlyRoleIds = viewOnlyRoles.map((role) => role.id);
        this.field.viewOnlyRoleIds = this.viewOnlyRoleIds;
        this.selectableRoles = this.allRoles.filter((role) => !this.viewOnlyRoleIds.includes(role.id));
    }

    onTypeChange(): void {
        this.hasMinMax = this.typeHasMinMax(this.field.type);
        this.disablePlaceholderInputForDropdownField(this.field.type);
    }

    getEmptyDataFieldOption(): DataFieldOption {
        return new DataFieldOption();
    }

    async onOptionRemove(dataFieldOptionIndex: number): Promise<void> {
        this.dataFieldOptionState.removeDataFieldOption(dataFieldOptionIndex);
    }

    protected async getRoles(): Promise<void> {
        const [roles] = await Role.where('company_id', this.auth.company.id).all();
        this.allRoles = roles;

        this.roleIds = this.field.roles.map((role) => role.id);
        this.viewOnlyRoleIds = this.field.viewOnlyRoles.map((role) => role.id);
        this.selectableRoles = this.allRoles.filter((role) => !this.viewOnlyRoleIds.includes(role.id));
        this.viewOnlySelectableRoles = this.allRoles.filter((role) => !this.roleIds.includes(role.id));
    }

    protected async getDataFieldGroups(): Promise<void> {
        const [customFieldGroups] = await DataFieldGroup.param('company', this.auth.company.id)
            .when(this.componentFilter, (query) => query.where('component', this.componentFilter))
            .where('onlyCustomGroups', true)
            .orderBy('name', 'ASC')
            .all();

        const [defaultFieldGroups] = await DataFieldGroup.param('company', this.auth.company.id)
            .when(this.componentFilter, (query) => query.where('component', this.componentFilter))
            .where('onlyDefaultGroups', true)
            .all();
        this.fieldGroups = [...defaultFieldGroups, ...customFieldGroups];
    }

    private typeHasMinMax(type: string): boolean {
        return type === DataFieldTypes.shortText || type === DataFieldTypes.number;
    }

    private disablePlaceholderInputForDropdownField(type: string): void {
        const defaultDropdownPlaceholder = 'Select an option';

        if (type === DataFieldTypes.dropdown) {
            this.field.description = defaultDropdownPlaceholder;
            this.placeholderInputDisabled = true;
            return;
        }

        if (type !== DataFieldTypes.dropdown && this.field.description === defaultDropdownPlaceholder) {
            this.field.description = '';
            this.placeholderInputDisabled = false;
        }
    }
}
